import { ListPagination, NewBadge } from "@/components/ListPagination";
import { useEffect, useState, Fragment } from "react";
import { Minimize2, Maximize2, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Sparkles, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function TableShell({ children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/**
 * Generic CRUD manager.
 * fields: [{ key, label, type: text|number|textarea|select, options?, placeholder?, ai? }]
 * columns: [{ key, label, render?, align? }]
 */
export default function EntityManager({
  title,
  subtitle,
  endpoint,
  entityName,
  fields,
  columns,
  defaults,
  onAiCategorize,
  rowActions,
  headerExtra,
  banner,
  onSaved,
  confirmSave,
  transformBeforeSave,
  windowControls,
  formExtra,
  formExtraAfter,
  formSide,
  dialogClassName,
  selectedActions,
  selectable = false,
  onPrintSelected,
  filters,
  exportCsv,
  footerRow,
  initialCreate,
  initialEditId,
  initialFilterVals,
  pageSize,
  jumpField,
}) {
  const [rows, setRows] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults || {});
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [filterVals, setFilterVals] = useState(initialFilterVals || {});
  const [page, setPage] = useState(1);
  const [jumpQ, setJumpQ] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [openedInitialEdit, setOpenedInitialEdit] = useState(false);
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const displayRows = (rows || []).filter((r) => (filters || []).every((f) => f.match(r, filterVals[f.key] ?? (f.type === "select" ? f.options?.[0] : ""), filterVals)));
  const allSel = displayRows.length > 0 && displayRows.every((r) => sel.has(r.id));
  const toggleAllSel = () => setSel((p) => { const n = new Set(p); const all = displayRows.every((r) => n.has(r.id)); displayRows.forEach((r) => (all ? n.delete(r.id) : n.add(r.id))); return n; });
  const selectedRows = displayRows.filter((r) => sel.has(r.id));
  const totalPages = pageSize ? Math.max(1, Math.ceil(displayRows.length / pageSize)) : 1;
  const curPage = Math.min(page, totalPages);
  const pageOffset = pageSize ? (curPage - 1) * pageSize : 0;
  const pagedRows = pageSize ? displayRows.slice(pageOffset, pageOffset + pageSize) : displayRows;

  const doJump = () => {
    if (!jumpField || !pageSize) return;
    const q = jumpQ.trim().toLowerCase();
    if (!q) return;
    const key = (r) => String(r[jumpField.key] || "").toLowerCase();
    let idx = displayRows.findIndex((r) => key(r) === q);
    if (idx === -1) idx = displayRows.findIndex((r) => key(r).includes(q));
    if (idx === -1) {
      toast.error(`"${jumpQ.trim()}" not found`);
      return;
    }
    const target = Math.floor(idx / pageSize) + 1;
    setPage(target);
    setHighlightId(displayRows[idx].id);
    toast.success(`Found on page ${target}`);
    setTimeout(() => setHighlightId(null), 5000);
  };

  const load = async () => {
    try {
      const res = await api.get(`/${endpoint}`);
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  useEffect(() => {
    setPage(1);
  }, [filterVals]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaults || {});
    setDialogOpen(true);
  };

  useEffect(() => {
    if (initialCreate) {
      setEditing(null);
      setForm({ ...(defaults || {}), ...initialCreate });
      setDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCreate]);

  useEffect(() => {
    if (!initialEditId || !rows || openedInitialEdit) return;
    const row = rows.find((r) => r.id === initialEditId);
    if (row) {
      setOpenedInitialEdit(true);
      openEdit(row);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditId, rows]);

  useEffect(() => {
    setOpenedInitialEdit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditId]);

  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...defaults, ...row });
    setDialogOpen(true);
  };

  const save = async () => {
    if (confirmSave && !confirmSave(form, rows || [], editing)) return;
    setSaving(true);
    try {
      let saved;
      const payload = transformBeforeSave ? transformBeforeSave(form) : form;
      if (editing) {
        const res = await api.put(`/${endpoint}/${editing.id}`, payload);
        saved = res.data;
        toast.success(`${entityName} updated`);
      } else {
        const res = await api.post(`/${endpoint}`, payload);
        saved = res.data;
        toast.success(`${entityName} added`);
      }
      setDialogOpen(false);
      load();
      if (onSaved && saved) onSaved(saved);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${entityName.toLowerCase()}?`)) return;
    try {
      await api.delete(`/${endpoint}/${row.id}`);
      toast.success(`${entityName} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const category = await onAiCategorize(form);
      setForm((f) => ({ ...f, category }));
      toast.success(`AI suggested: ${category}`);
    } catch (e) {
      toast.error("AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={
          <div className="flex items-center gap-2">
            {headerExtra && headerExtra(rows || [], displayRows, filterVals, load, setFilterVals)}
            {selectable && selectedActions && selectedActions(selectedRows, load)}
            {exportCsv && (
              <Button variant="outline" onClick={() => exportCsv(selectedRows.length ? selectedRows : displayRows)} disabled={!rows?.length} data-testid={`export-${entityName.toLowerCase()}-btn`}>
                <FileDown className="mr-2 h-4 w-4" /> Excel
              </Button>
            )}
            {selectable && onPrintSelected && (
              <Button variant="outline" onClick={() => onPrintSelected(selectedRows.length ? selectedRows : displayRows)} disabled={!rows?.length} data-testid={`print-${entityName.toLowerCase()}-btn`}>
                <Printer className="mr-2 h-4 w-4" /> Print{selectedRows.length ? ` (${selectedRows.length})` : ""}
              </Button>
            )}
            <Button data-testid={`add-${entityName.toLowerCase()}-btn`} onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> Add {entityName}
            </Button>
          </div>
        }
      />

      {banner && banner(rows || [])}

      {filters && filters.length > 0 && (
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card px-4 py-3">
          {filters.filter((f) => !f.hidden).map((f) => (
            <div key={f.key} className="grid gap-1">
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">{f.label}</Label>
              {f.type === "select" ? (
                <Select value={filterVals[f.key] ?? f.options?.[0] ?? ""} onValueChange={(v) => setFilterVals((s) => ({ ...s, [f.key]: v }))}>
                  <SelectTrigger className="h-9 w-[160px] text-sm" data-testid={`filter-${f.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "toggle" ? (
                <Button
                  type="button"
                  size="sm"
                  className={`h-9 text-xs font-bold ${filterVals[f.key] ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-amber-300 bg-white text-amber-700 hover:bg-amber-50"}`}
                  variant="ghost"
                  onClick={() => setFilterVals((s) => ({ ...s, [f.key]: s[f.key] ? "" : "1" }))}
                  data-testid={`filter-${f.key}`}
                >
                  {filterVals[f.key] ? "✓ " : ""}{f.toggleLabel || f.label}{f.badge ? ` (${f.badge(rows || [])})` : ""}
                </Button>
              ) : f.type === "text" ? (
                <Input type="text" placeholder={f.placeholder} className="h-9 w-[200px] text-sm" data-testid={`filter-${f.key}`} value={filterVals[f.key] ?? ""} onChange={(e) => setFilterVals((s) => ({ ...s, [f.key]: e.target.value }))} />
              ) : (
                <Input type="date" className="h-9 w-[160px] text-sm" data-testid={`filter-${f.key}`} value={filterVals[f.key] ?? ""} onChange={(e) => setFilterVals((s) => ({ ...s, [f.key]: e.target.value }))} />
              )}
            </div>
          ))}
          {jumpField && pageSize ? (
            <div className="grid gap-1">
              <Label className="text-[11px] uppercase tracking-wide text-slate-500">{jumpField.label}</Label>
              <div className="flex gap-1.5">
                <Input
                  className="h-9 w-[140px] text-sm"
                  placeholder="No."
                  value={jumpQ}
                  onChange={(e) => setJumpQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doJump()}
                  data-testid={`${endpoint}-jump-input`}
                />
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={doJump} title={jumpField.label} data-testid={`${endpoint}-jump-btn`}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => setFilterVals({})} data-testid="filter-clear">Clear filters</Button>
          <span className="ml-auto text-xs text-muted-foreground">{displayRows.length} record(s)</span>
        </div>
      )}

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {selectable && (
                <TableHead className="w-10">
                  <Checkbox checked={allSel} onCheckedChange={toggleAllSel} data-testid={`select-all-${entityName.toLowerCase()}`} aria-label="Select all" />
                </TableHead>
              )}
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${
                    c.align === "right" ? "text-right" : ""
                  }`}
                >
                  {c.label}
                </TableHead>
              ))}
              <TableHead className="w-[90px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3, 4].map((i) => (
                <TableRow key={i}>
                  {selectable && <TableCell />}
                  {columns.map((c) => (
                    <TableCell key={c.key}>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 1 + (selectable ? 1 : 0)} className="py-12 text-center text-muted-foreground">
                  No {entityName.toLowerCase()} records yet. Click “Add {entityName}” to create one.
                </TableCell>
              </TableRow>
            )}
            {rows &&
              pagedRows.map((row, rowIdx) => (
                <TableRow key={row.id} data-testid={`${endpoint}-row`} className={highlightId === row.id ? "bg-amber-100 hover:bg-amber-100" : ""} data-state={sel.has(row.id) ? "selected" : undefined}>
                  {selectable && (
                    <TableCell className="w-10">
                      <Checkbox checked={sel.has(row.id)} onCheckedChange={() => toggleSel(row.id)} data-testid={`select-${endpoint}-row`} aria-label="Select row" />
                    </TableCell>
                  )}
                  {columns.map((c, ci) => (
                    <TableCell
                      key={c.key}
                      className={`text-sm ${c.align === "right" ? "text-right tabular-nums" : ""}`}
                    >
                      {c.render ? c.render(row, pageOffset + rowIdx) : row[c.key] || "—"}
                      {ci === 0 && <NewBadge createdAt={row.created_at} />}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {rowActions && rowActions(row)}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(row)}
                        data-testid={`edit-${endpoint}-btn`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => remove(row)}
                        data-testid={`delete-${endpoint}-btn`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            {footerRow && displayRows.length > 0 && footerRow(displayRows)}
          </TableBody>
        </Table>
      </TableShell>
      {pageSize ? (
        <ListPagination total={displayRows.length} page={page} setPage={setPage} pageSize={pageSize} noun={`${entityName.toLowerCase()}s`} testPrefix={endpoint} />
      ) : null}

      <Dialog
        open={dialogOpen && !minimized}
        onOpenChange={(o) => {
          if (!o && minimized) return;
          setDialogOpen(o);
          if (!o) {
            setMinimized(false);
            setMaximized(false);
          }
        }}
      >
        <DialogContent className={maximized ? "h-[96vh] max-h-[96vh] overflow-y-auto sm:max-w-[96vw]" : `max-h-[90vh] overflow-y-auto ${dialogClassName || "sm:max-w-[520px]"}`}>
          {windowControls && (
            <div className="absolute right-10 top-3 flex gap-1">
              <button type="button" className="rounded p-1 text-muted-foreground hover:bg-slate-100" onClick={() => setMinimized(true)} data-testid="dialog-minimize-btn" title="Minimize">
                <Minimize2 className="h-4 w-4" />
              </button>
              <button type="button" className="rounded p-1 text-muted-foreground hover:bg-slate-100" onClick={() => setMaximized((m) => !m)} data-testid="dialog-maximize-btn" title={maximized ? "Restore size" : "Maximize"}>
                <Maximize2 className="h-4 w-4" />
              </button>
            </div>
          )}
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Edit ${entityName}` : `Add ${entityName}`}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const sidePanel = formSide ? formSide(form, setForm) : null;
            const fieldsBody = (
              <div className="grid content-start gap-4">
            {fields.map((f) => (
              f.showIf && !f.showIf(form) ? null : (
              <Fragment key={f.key}>
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  {f.ai && onAiCategorize && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                      disabled={aiLoading}
                      onClick={runAi}
                      data-testid="ai-categorize-btn"
                    >
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      {aiLoading ? "Thinking…" : "AI suggest"}
                    </Button>
                  )}
                </div>
                {f.type === "textarea" ? (
                  <Textarea
                    id={f.key}
                    data-testid={`field-${f.key}`}
                    placeholder={f.placeholder}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                ) : f.type === "lookup" ? (
                  <Select
                    value={form[f.key] ?? ""}
                    onValueChange={(v) => {
                      const opt = (f.options || []).find((o) => o.value === v);
                      setForm((prev) => ({ ...prev, [f.key]: v, ...(f.applyPatch ? f.applyPatch(opt, prev) : opt?.patch || {}) }));
                    }}
                  >
                    <SelectTrigger data-testid={`field-${f.key}`}>
                      <SelectValue placeholder={f.placeholder || "Select"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "items" ? (
                  <div className="rounded-md border p-2" data-testid={`field-${f.key}`}>
                    {(form[f.key] || []).length > 0 && (
                      <table className="w-full text-xs">
                        <thead><tr>{f.columns.map((c) => <th key={c.key} className="pb-1 pr-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</th>)}<th /></tr></thead>
                        <tbody>
                          {(form[f.key] || []).map((row, ri) => (
                            <tr key={ri}>
                              {f.columns.map((c) => (
                                <td key={c.key} className="pb-1 pr-1.5">
                                  <Input
                                    className="h-8 px-2 text-xs"
                                    type={c.type === "date" ? "date" : "text"}
                                    placeholder={c.placeholder}
                                    value={row[c.key] ?? ""}
                                    data-testid={`field-${f.key}-${c.key}-${ri}`}
                                    onChange={(e) => {
                                      const list = [...(form[f.key] || [])];
                                      list[ri] = { ...list[ri], [c.key]: e.target.value };
                                      setForm({ ...form, [f.key]: list });
                                    }}
                                  />
                                </td>
                              ))}
                              <td className="pb-1">
                                <button type="button" className="px-1 text-sm font-bold text-destructive" title="Remove row" data-testid={`field-${f.key}-remove-${ri}`}
                                  onClick={() => setForm({ ...form, [f.key]: (form[f.key] || []).filter((_, i) => i !== ri) })}>✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <Button type="button" variant="outline" size="sm" className="mt-1 h-7 text-xs" data-testid={`field-${f.key}-add-btn`}
                      onClick={() => setForm({ ...form, [f.key]: [...(form[f.key] || []), Object.fromEntries(f.columns.map((c) => [c.key, c.default ? c.default(form) : ""]))] })}>
                      + {f.addLabel || "Add Row"}
                    </Button>
                  </div>
                ) : f.type === "select" ? (
                  <Select
                    value={form[f.key] ?? ""}
                    onValueChange={(v) => setForm({ ...form, [f.key]: v === "__clear__" ? "" : v })}
                  >
                    <SelectTrigger data-testid={`field-${f.key}`}>
                      <SelectValue placeholder={f.placeholder || "Select"} />
                    </SelectTrigger>
                    <SelectContent>
                      {f.clearable && (
                        <SelectItem value="__clear__" className="text-red-600" data-testid={`field-${f.key}-clear`}>
                          ✕ {f.clearLabel || "None (remove)"}
                        </SelectItem>
                      )}
                      {[...f.options, ...(form[f.key] && !f.options.includes(form[f.key]) ? [form[f.key]] : [])].map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <>
                    <Input
                      id={f.key}
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                      data-testid={`field-${f.key}`}
                      placeholder={f.placeholder}
                      list={f.suggestions?.length ? `${f.key}-suggestions` : undefined}
                      value={form[f.key] ?? ""}
                      onBlur={f.autoFill ? async (e) => {
                        const patch = await f.autoFill(e.target.value, form);
                        if (patch) setForm((prev) => ({ ...prev, ...patch }));
                      } : undefined}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value,
                        })
                      }
                    />
                    {f.suggestions?.length ? (
                      <datalist id={`${f.key}-suggestions`}>
                        {f.suggestions.map((s) => (
                          <option key={s} value={s} />
                        ))}
                      </datalist>
                    ) : null}
                  </>
                )}
              </div>
              {formExtra && formExtraAfter === f.key && formExtra(form, setForm)}
              </Fragment>
              )
            ))}
            {formExtra && !formExtraAfter && formExtra(form, setForm)}
              </div>
            );
            return sidePanel ? (
              <div className="grid gap-4 py-2 lg:grid-cols-[1fr_1.05fr] lg:items-start">
                {fieldsBody}
                <div className="grid content-start gap-3">{sidePanel}</div>
              </div>
            ) : (
              <div className="grid gap-4 py-2">{fieldsBody}</div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-entity-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {windowControls && dialogOpen && minimized && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-border bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-lg"
          data-testid="dialog-restore-btn"
        >
          <Maximize2 className="h-3.5 w-3.5" /> {editing ? `Edit ${entityName}` : `New ${entityName}`} (draft) — Restore
        </button>
      )}
    </div>
  );
}
